import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslate } from '@tolgee/react';
import { AppRoute } from '@agentselly/immodossier-shared';
import { useLoaderQuery } from '~/helper/useLoaderQuery';
import { useAppParams } from '~/helper/useAppParams';
import { LoadingWidget } from '~/components/widget/base/LoadingWidget';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { RenderWidgets } from '~/components/widget/RenderWidgets';
import { cockpitActivitiesWidgets } from '~/config/widgets/cockpit/cockpitActivitiesWidgets';
import { useAuthz } from '~/context/AuthzContext';
import {
  Accordion,
  EmptyBlock,
  fmtTime,
  Ico,
  NewBadge,
  PageHead,
  V3Page,
  type IconName,
} from '~/components/cockpit/v3';
import {
  flattenActivities,
  type CockpitActivity,
  type WidgetProps,
} from '~/components/cockpit/cockpitData';
import {
  getCockpitActivityActor,
  getCockpitActivityTitle,
} from '~/components/widget/cockpit/cockpitActivityDesignDataConfig';

export const Route = createFileRoute(
  AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_ACTIVITIES,
)({
  component: ActivitiesPage,
});

const DAY = 864e5;
const OLDER_DAYS = 30;

const TYPE_ICON: Record<string, IconName> = {
  call: 'phone',
  email: 'mail',
  note: 'doc',
  meeting: 'cal',
  task: 'check',
};

const startOfDay = (ms: number): number => {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};
const startOfWeek = (ms: number): number => {
  const d = new Date(startOfDay(ms));
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d.getTime();
};
const daysAgo = (ms: number): number =>
  Math.round((startOfDay(Date.now()) - startOfDay(ms)) / DAY);

type T = (key: string, def?: string) => string;

function groupLabel(ms: number, t: T): string {
  const n = daysAgo(ms);
  if (n <= 0) return t('cockpit_act_today', 'Heute');
  if (n === 1) return t('cockpit_act_yesterday', 'Gestern');
  const w = startOfWeek(Date.now()) - startOfWeek(ms);
  if (w === 0) return t('cockpit_act_this_week', 'Diese Woche');
  if (w === 7 * DAY) return t('cockpit_act_last_week', 'Letzte Woche');
  return new Date(ms).toLocaleDateString('de-CH', {
    month: 'long',
    year: 'numeric',
  });
}

const str = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() ? v : undefined;

function Entry(props: {
  readonly a: CockpitActivity;
  readonly isAdvisor: boolean;
  readonly t: T;
}): React.ReactElement {
  const { a, isAdvisor, t } = props;
  const [
    open,
    setOpen,
  ] = useState(false);
  const typeLabel = getCockpitActivityTitle(t, a.type);
  const title = str(a.data?.title) ?? typeLabel;
  const desc =
    str(a.data?.description) ?? getCockpitActivityActor(t, a.type, a.person);
  const name = a.person?.trim();
  return (
    <li
      className="tle"
      data-type={a.type}
      data-od-id={`activity-${a.date}`}
    >
      <span
        className="tli"
        role="img"
        aria-label={typeLabel}
      >
        <Ico
          name={TYPE_ICON[a.type] ?? 'info'}
          size={14}
        />
      </span>
      <button
        type="button"
        className="tlb"
        aria-expanded={open}
        aria-label={`${title} – ${t(
          'cockpit_act_toggle',
          'Details ein-/ausblenden',
        )}`}
        onClick={() => setOpen(!open)}
      >
        <span className="t">{title}</span>
        <span className="desc">{desc}</span>
      </button>
      <p className="tlm">
        {fmtTime(a.date)}
        {name ? (
          <>
            {' · '}
            {t('cockpit_act_with', 'mit')}{' '}
            {isAdvisor && a.email ? (
              <a
                href={`mailto:${a.email}`}
                aria-label={`${t('cockpit_v3_mail_to', 'E-Mail an')} ${name}`}
              >
                {name}
              </a>
            ) : (
              name
            )}
          </>
        ) : null}
      </p>
    </li>
  );
}

function ActivitiesPage(): React.ReactElement {
  const params = useAppParams(Route);
  const { t } = useTranslate();
  const { atLeast } = useAuthz();
  const isAdvisor = atLeast('partner');
  const [
    filter,
    setFilter,
  ] = useState('all');

  const query = useLoaderQuery(
    AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_ACTIVITIES,
    { params },
  );

  if (query.isLoading) return <LoadingWidget />;
  if (query.isError || !query.data) return <ErrorWidget error={query.error} />;

  if (!query.data.v3Redesign) {
    const widgetProps = query.data?.widgetProps || {};
    const widgets = cockpitActivitiesWidgets(widgetProps);
    return (
      <div className="h-full flex flex-col gap-y-6">
        <RenderWidgets widgets={widgets} />
      </div>
    );
  }

  const wp = (query.data.widgetProps || {}) as WidgetProps;
  const all = flattenActivities(wp.cockpitActivitiesDetail?.activities);
  const head = (
    <PageHead
      odId="page-head"
      title={t('cockpit_act_title', 'Aktivitäten')}
      sub={t(
        'cockpit_act_sub',
        'Was zuletzt rund um Ihren Verkauf passiert ist',
      )}
    />
  );

  if (all.length === 0) {
    return (
      <V3Page>
        {head}
        <EmptyBlock
          icon="doc"
          title={t('cockpit_act_empty_title', 'Noch keine Aktivitäten')}
          text={t(
            'cockpit_act_empty_text',
            'Anrufe, E-Mails, Notizen, Termine und Aufgaben rund um den Verkauf erscheinen hier chronologisch, sobald Ihr Betreuer sie erfasst.',
          )}
        />
      </V3Page>
    );
  }

  const plural: Record<string, string> = {
    call: t('cockpit_act_p_call', 'Anrufe'),
    email: t('cockpit_act_p_email', 'E-Mails'),
    note: t('cockpit_act_p_note', 'Notizen'),
    meeting: t('cockpit_act_p_meeting', 'Termine'),
    task: t('cockpit_act_p_task', 'Aufgaben'),
  };
  const types = [...new Set(all.map((a) => a.type))];
  const chips: [string, string, number][] = [
    [
      'all',
      t('cockpit_act_f_all', 'Alle'),
      all.length,
    ],
    ...types.map((k): [string, string, number] => [
      k,
      plural[k] ?? getCockpitActivityTitle(t, k),
      all.filter((a) => a.type === k).length,
    ]),
  ];

  const hit = (a: CockpitActivity): boolean =>
    filter === 'all' || a.type === filter;
  // The feed carries no task status; every task counts as open unless marked COMPLETED.
  const tasks = all.filter(
    (a) => a.type === 'task' && a.data?.status !== 'COMPLETED',
  );
  const rest = all.filter((a) => a.type !== 'task').filter(hit);
  const recent = rest.filter((a) => daysAgo(a.date) <= OLDER_DAYS);
  const older = rest.filter((a) => daysAgo(a.date) > OLDER_DAYS);
  const showTasks = tasks.length > 0 && (filter === 'all' || filter === 'task');

  const groups = (list: CockpitActivity[]): React.ReactElement[] => {
    const g = new Map<string, CockpitActivity[]>();
    list.forEach((a) => {
      const k = groupLabel(a.date, t);
      g.set(k, [
        ...(g.get(k) ?? []),
        a,
      ]);
    });
    return [...g.entries()].map(
      ([
        k,
        items,
      ]) => (
        <section
          className="tlg"
          key={k}
          aria-label={k}
        >
          <h3>{k}</h3>
          <ul className="tll">
            {items.map((a, i) => (
              <Entry
                key={`${a.date}-${i}`}
                a={a}
                isAdvisor={isAdvisor}
                t={t}
              />
            ))}
          </ul>
        </section>
      ),
    );
  };

  const empty = !showTasks && recent.length + older.length === 0;

  return (
    <V3Page>
      {head}
      {showTasks ? (
        <section
          className="card"
          id="tasks"
          data-od-id="open-tasks"
          aria-labelledby="h-tk"
        >
          <div
            className="sec"
            style={{ marginBottom: 12 }}
          >
            <h2 id="h-tk">
              {t('cockpit_act_tasks', 'Offene Aufgaben')}
              <NewBadge />
            </h2>
          </div>
          {tasks.map((a, i) => (
            <div
              className="tk"
              key={`${a.date}-${i}`}
              data-type="task"
              data-od-id={`activity-${a.date}`}
            >
              <span
                className="tli"
                role="img"
                aria-label={plural.task}
              >
                <Ico
                  name="check"
                  size={14}
                />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p className="t">
                  {str(a.data?.title) ?? getCockpitActivityTitle(t, a.type)}
                </p>
                <p className="desc">
                  {str(a.data?.description) ??
                    getCockpitActivityActor(t, a.type, a.person)}
                </p>
                <p className="xs mu">
                  {t('cockpit_act_created', 'Angelegt am')}{' '}
                  {new Date(a.date).toLocaleDateString('de-CH', {
                    day: 'numeric',
                    month: 'short',
                  })}
                </p>
              </div>
            </div>
          ))}
        </section>
      ) : null}

      <section
        className="card"
        data-od-id="activity-timeline"
        data-widget="cockpitActivitiesDetail"
        aria-labelledby="h-tl"
      >
        <div
          className="sec"
          style={{ marginBottom: 16 }}
        >
          <h2 id="h-tl">{t('cockpit_act_timeline', 'Verlauf')}</h2>
        </div>
        <div
          className="fch"
          role="group"
          aria-label={t('cockpit_act_filter', 'Nach Typ filtern')}
          data-od-id="activity-filter"
        >
          {chips.map(
            ([
              k,
              label,
              n,
            ]) => (
              <button
                key={k}
                type="button"
                aria-pressed={k === filter}
                onClick={() => setFilter(k)}
              >
                {label}
                <span className="num">{n}</span>
              </button>
            ),
          )}
        </div>
        <div style={{ marginTop: 24 }}>{groups(recent)}</div>
        {older.length ? (
          <div style={{ marginTop: 24 }}>
            <Accordion
              odId="older-accordion"
              label={t('cockpit_act_older', 'Ältere Aktivitäten')}
              count={older.length}
            >
              {groups(older)}
            </Accordion>
          </div>
        ) : null}
        {empty ? (
          <p
            className="sm mu"
            style={{ padding: '12px 0' }}
          >
            {t('cockpit_act_nohit', 'Keine Aktivitäten dieses Typs.')}
          </p>
        ) : null}
      </section>
    </V3Page>
  );
}
